import {
  Component,
  HostBinding,
  inject,
  input,
  OnInit,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';

import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';

import { Error } from '@factor_ec/utils';
import { IconComponent } from '@factor_ec/ui';

import { SessionService } from '@/core/session.service';
/**
 * @description Componente para mostrar páginas de error genéricas
 * @class ErrorComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-error',
  imports: [IconComponent, MatButtonModule, RouterModule],
  templateUrl: './error.component.html',
  styleUrl: './error.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ErrorComponent implements OnInit {
  // Dependency injection
  sessionService = inject(SessionService);
  title = inject(Title);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  // Properties
  error = signal<Error | undefined>(undefined);
  message!: string;
  class = input<string>();

  @HostBinding('class') get hostClasses(): string {
    return ['ft-page', this.class()].join(' ');
  }

  /**
   * @description Inicializa el componente
   * @implements OnInit
   */
  async ngOnInit(): Promise<void> {
    // Obtiene el mensaje de error de la navegación si existe
    if (this.router.currentNavigation()?.extras.state?.['message']) {
      this.message = this.router.currentNavigation()?.extras.state?.['message'];
    }

    // Obtiene el código de error de los parámetros o datos de ruta
    let code = -1;
    if (this.route.snapshot.params['code']) {
      code = Number(this.route.snapshot.params['code']);
    } else if (this.route.snapshot.data['code']) {
      code = Number(this.route.snapshot.data['code']);
    }

    // Configura el error según el código
    switch (Number(code)) {
      case 0:
        this.error.set({
          icon: '0',
          title: $localize`Closing session`,
          message:
            this.message ||
            $localize`Your session is being closed. Please wait a moment.`
        });
        break;
      case 400:
        this.error.set({
          icon: '400',
          title: $localize`Bad Request`,
          message:
            this.message ||
            $localize`The request failed, please try again or contact the administrator.`
        });
        break;
      case 403:
        this.error.set({
          icon: '403',
          title: $localize`Forbidden`,
          message:
            this.message ||
            $localize`You do not have permission to access this content.`
        });
        break;
      case 404:
        this.error.set({
          icon: '404',
          title: $localize`Not Found`,
          message:
            this.message ||
            $localize`The content you are looking for cannot be found on this site.`
        });
        break;
      case 412:
        this.error.set({
          icon: '412',
          title: $localize`Precondition Failed`,
          message:
            this.message ||
            $localize`The request could not be completed due to a failed precondition.`
        });
        break;
      case 503:
        this.error.set({
          icon: '503',
          title: $localize`Service Unavailable`,
          message:
            this.message ||
            $localize`The server is currently unable to handle the request due to a temporary overload or maintenance of the server.`
        });
        break;
      default:
        this.error.set({
          icon: 'unknown',
          title: $localize`Unknown Error`,
          message: $localize`The server cannot handle the request due to an unknown error.`
        });
        break;
    }

    // Establece el título de la página
    this.title.setTitle(this.error()?.title ?? 'Error');
  }

  goHome(): void {
    location.href = '/';
  }

  /**
   * @description Recarga la página actual
   */
  reload(): void {
    location.reload();
  }
}
